import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { verifyCode, createVerificationCode, checkExistingCode, markCodeAsUsed, sendSmsCodeViaFunction } from '../utils/smsVerificationAPI';
import { createNewUserWithPhone } from '../utils/profilesAPI';

function Register() {
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeCountdown, setCodeCountdown] = useState(0);
  const navigate = useNavigate();

  // 使用本地API或Edge Function发送验证码
  const handleSendCode = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入有效的手机号');
      return;
    }
    setError(null);
    setCodeLoading(true);
    
    try {
      // 检查是否有现有的验证码
      const existingCodeResult = await checkExistingCode(phone);
      
      if (existingCodeResult.success && existingCodeResult.hasExistingCode && existingCodeResult.cooldownRemaining > 0) {
        setError(`请${existingCodeResult.cooldownRemaining}秒后再试`);
        // 设置倒计时
        setCodeCountdown(existingCodeResult.cooldownRemaining);
        const timer = setInterval(() => {
          setCodeCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        setCodeLoading(false);
        return;
      }
      
      // 尝试使用本地API创建验证码
      let result;
      try {
        // 优先使用本地API
        result = await createVerificationCode(phone);
      } catch (localApiError) {
        console.error('本地API发送验证码失败:', localApiError);
        
        // 如果本地API失败，尝试使用Edge Function
        const data = await sendSmsCodeViaFunction(phone);
        
        if (!data.success) throw new Error(data.message || '验证码发送失败');
        
        result = data;
      }
      
      if (!result.success) {
        throw new Error(result.message || result.error || '验证码发送失败');
      }

      // 提示用户验证码（仅开发环境）
      if (result.code) {
        // 可以在控制台显示，或者使用更友好的提示
        console.log('验证码:', result.code);
      }

      // 发送成功，开始倒计时
      setCodeCountdown(60);
      const timer = setInterval(() => {
        setCodeCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      setError(err.message || '验证码发送失败');
    } finally {
      setCodeLoading(false);
    }
  };

  // 注册
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入有效的手机号');
      return;
    }
    if (!code) {
      setError('请输入验证码');
      return;
    }
    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }
    if (password.length < 6) {
      setError('密码长度至少为6位');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 1. 校验验证码
      const verifyResult = await verifyCode(phone, code);

      if (!verifyResult.success || !verifyResult.data) {
        throw new Error('验证码无效或已过期');
      }

      // 2. 创建用户 - 使用API函数
      const hashedPassword = await hashPassword(password);
      const createResult = await createNewUserWithPhone(phone, hashedPassword);
      
      if (!createResult.success) {
        throw new Error(createResult.error);
      }

      // 3. 将验证码标记为已使用
      await markCodeAsUsed(verifyResult.data.id);

      // 注册成功，跳转到登录页
      navigate('/login', { state: { message: '注册成功，请登录' } });
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 简单的密码哈希函数
  const hashPassword = async (password) => {
    // 使用 SHA-256 哈希密码
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title="注册" />
      <div className="flex-1 p-6 flex flex-col justify-center">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">创建账号</h2>
          <p className="text-gray-600">加入我们开始您的学习之旅</p>
        </div>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              手机号
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
              required
              maxLength={11}
              placeholder="请输入手机号"
            />
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                验证码
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
                required
                maxLength={6}
                placeholder="请输入验证码"
              />
            </div>
            <button
              type="button"
              className={`mt-6 px-4 py-2 rounded-lg text-white font-semibold transition-all ${codeCountdown > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'}`}
              onClick={handleSendCode}
              disabled={codeCountdown > 0 || codeLoading}
            >
              {codeLoading ? '发送中...' : codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码'}
            </button>
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              密码
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
              required
              placeholder="请设置密码，至少6位"
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              确认密码
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-light"
              required
              placeholder="请再次输入密码"
            />
          </div>
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-primary-dark to-primary py-3 rounded-lg text-white font-semibold shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
            disabled={loading}
          >
            {loading ? '注册中...' : '注册'}
          </button>
        </form>
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            已有账号？{' '}
            <Link to="/login" className="text-primary-dark font-semibold hover:underline">
              立即登录
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register; 